import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

// Attach token to every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Resolve a media/file URL: absolute URLs pass through; relative (/api/uploads/...) get the backend origin.
export const mediaUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
};

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  uploadAvatar: (file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post('/auth/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Users
export const usersAPI = {
  list: (role) => api.get('/users', { params: { role } }),
  updateRole: (userId, role) => api.put(`/users/${userId}/role`, { role }),
};

// Members
export const membersAPI = {
  create: (data) => api.post('/members', data),
  list: (params) => api.get('/members', { params }),
  get: (id) => api.get(`/members/${id}`),
  update: (id, data) => api.put(`/members/${id}`, data),
  delete: (id) => api.delete(`/members/${id}`),
  uploadAvatar: (memberId, file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/members/${memberId}/avatar`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Attendance
export const attendanceAPI = {
  record: (data) => api.post('/attendance', data),
  bulkRecord: (data) => api.post('/attendance/bulk', data),
  list: (params) => api.get('/attendance', { params }),
  stats: () => api.get('/attendance/stats'),
};

// Giving
export const givingAPI = {
  create: (data) => api.post('/giving', data),
  list: (params) => api.get('/giving', { params }),
  stats: () => api.get('/giving/stats'),
  stripeIntent: (data) => api.post('/giving/stripe/create-intent', data),
  paystackInit: (data) => api.post('/giving/paystack/initialize', data),
};

// Events
export const eventsAPI = {
  create: (data) => api.post('/events', data),
  list: () => api.get('/events'),
  get: (id) => api.get(`/events/${id}`),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
  register: (id) => api.post(`/events/${id}/register`),
  unregister: (id) => api.post(`/events/${id}/unregister`),
};

// Groups
export const groupsAPI = {
  create: (data) => api.post('/groups', data),
  list: (type) => api.get('/groups', { params: { type } }),
  get: (id) => api.get(`/groups/${id}`),
  update: (id, data) => api.put(`/groups/${id}`, data),
  delete: (id) => api.delete(`/groups/${id}`),
  addMember: (groupId, memberId) => api.post(`/groups/${groupId}/members`, { member_id: memberId }),
  removeMember: (groupId, memberId) => api.delete(`/groups/${groupId}/members/${memberId}`),
};

// Announcements
export const announcementsAPI = {
  create: (data) => api.post('/announcements', data),
  list: () => api.get('/announcements'),
  delete: (id) => api.delete(`/announcements/${id}`),
};

// Prayer Requests
export const prayerAPI = {
  create: (data) => api.post('/prayer-requests', data),
  list: () => api.get('/prayer-requests'),
  pray: (id) => api.post(`/prayer-requests/${id}/pray`),
  delete: (id) => api.delete(`/prayer-requests/${id}`),
};

// Testimonies
export const testimoniesAPI = {
  create: (data) => api.post('/testimonies', data),
  list: () => api.get('/testimonies'),
  like: (id) => api.post(`/testimonies/${id}/like`),
  delete: (id) => api.delete(`/testimonies/${id}`),
};

// Sermons
export const sermonsAPI = {
  create: (data) => api.post('/sermons', data),
  list: (series) => api.get('/sermons', { params: { series } }),
  get: (id) => api.get(`/sermons/${id}`),
  update: (id, data) => api.put(`/sermons/${id}`, data),
  delete: (id) => api.delete(`/sermons/${id}`),
};

// Media/YouTube
export const mediaAPI = {
  getLive: () => api.get('/media/youtube/live'),
  getVideos: () => api.get('/media/youtube/videos'),
};

// Bible
export const bibleAPI = {
  search: (q) => api.get('/bible/search', { params: { q } }),
  dailyVerse: () => api.get('/bible/daily-verse'),
  saveBookmark: (data) => api.post('/bible/bookmarks', data),
  getBookmarks: () => api.get('/bible/bookmarks'),
  deleteBookmark: (id) => api.delete(`/bible/bookmarks/${id}`),
};

// Consultations
export const consultationsAPI = {
  create: (data) => api.post('/consultations', data),
  list: () => api.get('/consultations'),
  updateStatus: (id, status) => api.put(`/consultations/${id}/status`, { status }),
  getAgoraToken: (data) => api.post('/consultations/agora-token', data),
};

// Settings
export const settingsAPI = {
  get: () => api.get('/settings'),
  save: (data) => api.post('/settings', data),
  saveBulk: (data) => api.post('/settings/bulk', data),
  getChurchProfile: () => api.get('/settings/church-profile'),
};

// Reports
export const reportsAPI = {
  dashboard: () => api.get('/reports/dashboard'),
  attendance: (params) => api.get('/reports/attendance', { params }),
  financial: (params) => api.get('/reports/financial', { params }),
  growth: () => api.get('/reports/growth'),
  generate: (data) => api.post('/reports/generate', data),
  donorAnalytics: () => api.get('/reports/donor-analytics'),
  engagementScores: () => api.get('/reports/engagement-scores'),
};

// AI
export const aiAPI = {
  chat: (data) => api.post('/ai/chat', data),
  getSessions: () => api.get('/ai/sessions'),
  getSessionMessages: (sessionId) => api.get(`/ai/sessions/${sessionId}`),
  deleteSession: (sessionId) => api.delete(`/ai/sessions/${sessionId}`),
  summarizeSermon: (data) => api.post('/ai/sermon/summarize', data),
  generateStudyGuide: (data) => api.post('/ai/bible/study-guide', data),
  generateDevotional: (data) => api.post('/ai/devotional/generate', data),
  getGenerated: (type) => api.get('/ai/generated', { params: { type } }),
  getTaskStatus: (taskId) => api.get(`/enterprise/ai-task/${taskId}`),
};

// Visitors
export const visitorsAPI = {
  create: (data) => api.post('/visitors', data),
  list: (params) => api.get('/visitors', { params }),
  update: (id, data) => api.put(`/visitors/${id}`, data),
  convert: (id) => api.post(`/visitors/${id}/convert`),
  delete: (id) => api.delete(`/visitors/${id}`),
};

// Families
export const familiesAPI = {
  create: (data) => api.post('/families', data),
  list: () => api.get('/families'),
  get: (id) => api.get(`/families/${id}`),
  update: (id, data) => api.put(`/families/${id}`, data),
  addMember: (familyId, data) => api.post(`/families/${familyId}/members`, data),
  removeMember: (familyId, memberId) => api.delete(`/families/${familyId}/members/${memberId}`),
  delete: (id) => api.delete(`/families/${id}`),
};

// Volunteers
export const volunteersAPI = {
  create: (data) => api.post('/volunteers', data),
  list: (team) => api.get('/volunteers', { params: { team } }),
  update: (id, data) => api.put(`/volunteers/${id}`, data),
  delete: (id) => api.delete(`/volunteers/${id}`),
};

// Tasks / Follow-ups
export const tasksAPI = {
  create: (data) => api.post('/tasks', data),
  list: (params) => api.get('/tasks', { params }),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
};

// Pledges
export const pledgesAPI = {
  create: (data) => api.post('/pledges', data),
  list: (params) => api.get('/pledges', { params }),
  recordPayment: (id, data) => api.post(`/pledges/${id}/payment`, data),
  delete: (id) => api.delete(`/pledges/${id}`),
};

// Service Plans
export const servicePlansAPI = {
  create: (data) => api.post('/service-plans', data),
  list: () => api.get('/service-plans'),
  get: (id) => api.get(`/service-plans/${id}`),
  update: (id, data) => api.put(`/service-plans/${id}`, data),
  delete: (id) => api.delete(`/service-plans/${id}`),
};

// Messages
export const messagesAPI = {
  send: (data) => api.post('/messages', data),
  getConversations: () => api.get('/messages/conversations'),
  getMessages: (convId) => api.get(`/messages/conversation/${convId}`),
};

// Notifications
export const notificationsAPI = {
  list: () => api.get('/notifications'),
  unreadCount: () => api.get('/notifications/unread-count'),
  markAllRead: () => api.put('/notifications/mark-read'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
};

// Exports
export const exportsAPI = {
  giving: () => api.get('/exports/giving', { responseType: 'blob' }),
  members: () => api.get('/exports/members', { responseType: 'blob' }),
  attendance: () => api.get('/exports/attendance', { responseType: 'blob' }),
  membersPDF: () => api.get('/exports/members/pdf', { responseType: 'blob' }),
  givingPDF: () => api.get('/exports/giving/pdf', { responseType: 'blob' }),
  attendancePDF: (date) => api.get('/exports/attendance/pdf', { responseType: 'blob', params: { date } }),
  engagementPDF: () => api.get('/exports/engagement/pdf', { responseType: 'blob' }),
};

// Users Management (Full)
export const usersManagementAPI = {
  list: (params) => api.get('/users/', { params }),
  create: (data) => api.post('/users/', data),
  get: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, data) => api.post(`/users/${id}/reset-password`, data),
  stats: () => api.get('/users/stats/summary'),
};

// Ministries
export const ministriesAPI = {
  create: (data) => api.post('/ministries/', data),
  list: (params) => api.get('/ministries/', { params }),
  get: (id) => api.get(`/ministries/${id}`),
  update: (id, data) => api.put(`/ministries/${id}`, data),
  delete: (id) => api.delete(`/ministries/${id}`),
  addMember: (ministryId, memberId) => api.post(`/ministries/${ministryId}/members/${memberId}`),
  removeMember: (ministryId, memberId) => api.delete(`/ministries/${ministryId}/members/${memberId}`),
};

// Assets/Inventory
export const assetsAPI = {
  create: (data) => api.post('/assets/', data),
  list: (params) => api.get('/assets/', { params }),
  get: (id) => api.get(`/assets/${id}`),
  update: (id, data) => api.put(`/assets/${id}`, data),
  delete: (id) => api.delete(`/assets/${id}`),
  addMaintenance: (id, data) => api.post(`/assets/${id}/maintenance`, data),
  stats: () => api.get('/assets/stats/summary'),
};

// Appointments/Counseling
export const appointmentsAPI = {
  create: (data) => api.post('/appointments/', data),
  list: (params) => api.get('/appointments/', { params }),
  get: (id) => api.get(`/appointments/${id}`),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  complete: (id, notes) => api.post(`/appointments/${id}/complete`, notes),
  cancel: (id) => api.delete(`/appointments/${id}`),
  stats: () => api.get('/appointments/stats/summary'),
};

// Campaigns
export const campaignsAPI = {
  create: (data) => api.post('/campaigns/', data),
  list: (params) => api.get('/campaigns/', { params }),
  get: (id) => api.get(`/campaigns/${id}`),
  send: (id) => api.post(`/campaigns/${id}/send`),
  delete: (id) => api.delete(`/campaigns/${id}`),
};

// Documents
export const documentsAPI = {
  create: (data) => api.post('/documents/', data),
  list: (params) => api.get('/documents/', { params }),
  get: (id) => api.get(`/documents/${id}`),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`),
};

// Payments (Stripe & Paystack)
export const paymentsAPI = {
  stripeCheckout: (data) => api.post('/payments/stripe/checkout', data),
  stripeStatus: (sessionId) => api.get(`/payments/stripe/status/${sessionId}`),
  paystackInitialize: (data) => api.post('/payments/paystack/initialize', data),
  paystackVerify: (reference) => api.get(`/payments/paystack/verify/${reference}`),
  transactions: (params) => api.get('/payments/transactions', { params }),
  stats: () => api.get('/payments/stats'),
};

// Agora (Live Streaming)
export const agoraAPI = {
  generateToken: (data) => api.post('/agora/token', data),
  startLiveStream: (data) => api.post('/agora/live-stream/start', data),
  endLiveStream: (sessionId) => api.post(`/agora/live-stream/${sessionId}/end`),
  initiateConsultation: (data) => api.post('/agora/consultation/initiate', data),
  listSessions: (params) => api.get('/agora/sessions', { params }),
  getSession: (sessionId) => api.get(`/agora/sessions/${sessionId}`),
  stats: () => api.get('/agora/stats'),
};

// Automation
export const automationAPI = {
  createRule: (data) => api.post('/automation/rules', data),
  listRules: (params) => api.get('/automation/rules', { params }),
  updateRule: (ruleId, data) => api.put(`/automation/rules/${ruleId}`, data),
  deleteRule: (ruleId) => api.delete(`/automation/rules/${ruleId}`),
  executeBirthday: () => api.post('/automation/execute/birthday'),
  executeVisitorFollowup: () => api.post('/automation/execute/visitor-followup'),
  executeAttendanceAlert: () => api.post('/automation/execute/attendance-alert'),
  executeGivingReminder: () => api.post('/automation/execute/giving-reminder'),
  executeEngagementAlert: () => api.post('/automation/execute/engagement-alert'),
  executeAll: () => api.post('/automation/execute-all'),
  logs: (params) => api.get('/automation/logs', { params }),
  stats: () => api.get('/automation/stats'),
};

// Check-In
export const checkinAPI = {
  getQR: (eventId) => api.get(`/checkin/qr/${eventId}`),
  checkIn: (data) => api.post('/checkin', data),
  selfCheckIn: (data) => api.post('/checkin/self', data),
  getEventCheckins: (eventId) => api.get(`/checkin/event/${eventId}`),
  stats: () => api.get('/checkin/stats'),
};

// Push Notifications
export const pushAPI = {
  registerDevice: (data) => api.post('/push/register-device', data),
  send: (data) => api.post('/push/send', data),
  history: () => api.get('/push/history'),
  devices: () => api.get('/push/devices'),
};

// AI Analytics
export const aiAnalyticsAPI = {
  weeklyData: () => api.get('/ai-analytics/weekly-data'),
  generateSummary: (data) => api.post('/ai-analytics/generate-summary', data || {}),
  summaries: () => api.get('/ai-analytics/summaries'),
  predictive: () => api.get('/ai-analytics/predictive'),
};

// Devotional Plans
export const devotionalAPI = {
  list: () => api.get('/devotional-plans'),
  create: (data) => api.post('/devotional-plans', data),
  subscribe: (id) => api.post(`/devotional-plans/${id}/subscribe`),
  progress: () => api.get('/devotional-plans/my-progress'),
  completeDay: (id, day) => api.post(`/devotional-plans/${id}/complete-day`, { day }),
};

// Fasting
export const fastingAPI = {
  list: () => api.get('/fasting'),
  create: (data) => api.post('/fasting', data),
  join: (id) => api.post(`/fasting/${id}/join`),
  log: (id, data) => api.post(`/fasting/${id}/log`, data),
};

// Discipleship
export const discipleshipAPI = {
  get: (memberId) => api.get(`/discipleship/${memberId}`),
  updateMilestone: (memberId, data) => api.post(`/discipleship/${memberId}/milestone`, data),
  stats: () => api.get('/discipleship-stats'),
};

// Forum
export const forumAPI = {
  posts: (topic) => api.get('/forum/posts', { params: { topic } }),
  create: (data) => api.post('/forum/posts', data),
  reply: (id, content) => api.post(`/forum/posts/${id}/reply`, { content }),
  like: (id) => api.post(`/forum/posts/${id}/like`),
};

// Budget
export const budgetAPI = {
  expenses: (category) => api.get('/budget/expenses', { params: { category } }),
  createExpense: (data) => api.post('/budget/expenses', data),
  summary: () => api.get('/budget/summary'),
};

// Room Booking
export const roomsAPI = {
  list: () => api.get('/rooms/list'),
  bookings: (date, room) => api.get('/rooms/bookings', { params: { date, room } }),
  book: (data) => api.post('/rooms/book', data),
  cancel: (id) => api.delete(`/rooms/bookings/${id}`),
};

// Background Checks
export const bgCheckAPI = {
  list: () => api.get('/background-checks'),
  create: (data) => api.post('/background-checks', data),
  update: (id, data) => api.put(`/background-checks/${id}`, data),
};

// Public endpoints (no auth)
export const publicAPI = {
  sermons: () => api.get('/public/sermons'),
  events: () => api.get('/public/events'),
};

// Uploads
export const uploadsAPI = {
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/uploads/file', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadBase64: (data) => api.post('/uploads/base64', data),
  list: (category) => api.get('/uploads/list', { params: { category } }),
  delete: (id) => api.delete(`/uploads/${id}`),
};

// CMS
export const cmsAPI = {
  sections: () => api.get('/cms/sections'),
  publicSections: () => api.get('/cms/public'),
  upsertSection: (data) => api.post('/cms/sections', data),
  deleteSection: (id) => api.delete(`/cms/sections/${id}`),
  initialize: () => api.post('/cms/initialize'),
  getSEO: () => api.get('/cms/seo'),
  updateSEO: (data) => api.post('/cms/seo', data),
  getCustomCSS: () => api.get('/cms/custom-css'),
  updateCustomCSS: (css) => api.post('/cms/custom-css', { custom_css: css }),
  getAnalytics: () => api.get('/cms/analytics'),
  updateAnalytics: (data) => api.post('/cms/analytics', data),
};

// Podcasts
export const podcastsAPI = {
  list: (params) => api.get('/podcasts/', { params }),
  create: (data) => api.post('/podcasts/', data),
  get: (id) => api.get(`/podcasts/${id}`),
  update: (id, data) => api.put(`/podcasts/${id}`, data),
  delete: (id) => api.delete(`/podcasts/${id}`),
};

// Chat
export const chatAPI = {
  getConversations: () => api.get('/chat/conversations'),
  getMessages: (convId) => api.get(`/chat/conversations/${convId}/messages`),
  sendMessage: (data) => api.post('/chat/messages', data),
  createConversation: (data) => api.post('/chat/conversations', data),
};

// Prophecy
export const prophecyAPI = {
  submit: (data) => api.post('/prophecies', data),
  list: (params) => api.get('/prophecies', { params }),
  review: (id, data) => api.put(`/prophecies/${id}/review`, data),
  journal: () => api.get('/prophecies/journal'),
  addJournal: (data) => api.post('/prophecies/journal', data),
};

// Health / System
export const healthAPI = {
  check: () => api.get('/health'),
  ready: () => api.get('/health/ready'),
  metrics: () => api.get('/health/metrics'),
  systemInfo: () => api.get('/system/info'),
};

// Data Export
export const dataExportAPI = {
  membersCSV: () => api.get('/data-export/members/csv', { responseType: 'blob' }),
  givingCSV: () => api.get('/data-export/giving/csv', { responseType: 'blob' }),
  attendanceCSV: () => api.get('/data-export/attendance/csv', { responseType: 'blob' }),
  fullBackup: () => api.get('/data-export/full-backup/json', { responseType: 'blob' }),
  memberData: (memberId) => api.get(`/data-export/member-data/${memberId}`, { responseType: 'blob' }),
  auditLog: () => api.get('/data-export/audit-log/csv', { responseType: 'blob' }),
};

// Spiritual Growth
export const spiritualGrowthAPI = {
  devotionals: () => api.get('/devotional-plans'),
  createDevotional: (data) => api.post('/devotional-plans', data),
  subscribe: (id) => api.post(`/devotional-plans/${id}/subscribe`),
  progress: () => api.get('/devotional-plans/my-progress'),
  completeDay: (id, day) => api.post(`/devotional-plans/${id}/complete-day`, { day }),
  fasting: () => api.get('/fasting'),
  createFasting: (data) => api.post('/fasting', data),
  joinFasting: (id) => api.post(`/fasting/${id}/join`),
  discipleship: (memberId) => api.get(`/discipleship/${memberId}`),
  updateMilestone: (memberId, data) => api.post(`/discipleship/${memberId}/milestone`, data),
  stats: () => api.get('/discipleship-stats'),
};
